/**
 * apiClient.js — Central HTTP wrapper for AI Farm frontend.
 *
 * A6 Sub-Phase 2 (Layers A + B + C foundation).
 *
 * Replaces ~120 raw fetch() write call-sites + ~254 manual Authorization-header
 * injections across 70 files. Provides:
 *
 *   Layer A — Foundation:
 *     - GET / POST / PATCH / DELETE methods
 *     - Auto-injected Authorization: Bearer <token> via token-provider
 *     - Auto-injected Content-Type: application/json for writes
 *     - Auto-JSON-unwrap on success
 *     - Structured ApiError on failure (status + body + idempotencyKey)
 *
 *   Layer B — Idempotency:
 *     - Client-generated UUID via crypto.randomUUID()
 *     - X-Idempotency-Key header on POST/PATCH/DELETE
 *     - Same key returned in ApiError on failure (for queue dedup later)
 *     - Backend middleware comes in Sub-Phase 4 (aif_idempotency_keys table)
 *
 *   Layer C — Offline-detection + IndexedDB queue:
 *     - navigator.onLine check before fetch
 *     - Fetch-failure detection (TypeError on network, AbortError on timeout)
 *     - Persistent IndexedDB queue via offlineQueueDB (A6 Sub-Phase 3)
 *     - Scope (tenantId + userId) auto-resolved via getCurrentScope()
 *
 *  Token-provider pattern: default reads localStorage('aif_token').
 *  App.jsx (or any consumer) can call setTokenProvider(fn) to override
 *  (e.g. to read from React-state for reactive updates).
 */

// =============================================================================
// Configuration
// =============================================================================

const API_BASE = import.meta.env.VITE_API_URL ?? '';
const DEFAULT_TIMEOUT_MS = 30000;
const TOKEN_STORAGE_KEY = 'aif_token';

// =============================================================================
// Token Provider (Layer A)
// =============================================================================

let tokenProvider = () => {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Override the token provider. Useful for testing or for App.jsx to use
 * React state directly (for reactive updates on login/logout).
 */
export function setTokenProvider(fn) {
  if (typeof fn !== 'function') {
    throw new Error('setTokenProvider: argument must be a function returning a token string or null');
  }
  tokenProvider = fn;
}

/**
 * Reset to default (localStorage). Used by tests; should not be called in app code.
 */
export function resetTokenProvider() {
  tokenProvider = () => {
    try {
      return localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  };
}

// =============================================================================
// Idempotency Key Generation (Layer B)
// =============================================================================

/**
 * Generate a UUID for idempotency key. Uses crypto.randomUUID (Browser native,
 * Node 19+). Falls back to a pseudo-UUID for very old environments.
 */
function generateIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback (very rare; older Node test envs)
  return 'fallback-' + Date.now() + '-' + Math.random().toString(36).slice(2, 11);
}

// =============================================================================
// ApiError (Layer A)
// =============================================================================

// =============================================================================
// A9-10: 402 -> upsell-event dispatch
// =============================================================================
function dispatchUpsellEvent(status, body) {
  if (status !== 402 || typeof window === 'undefined') return;
  const detail = body?.detail || body || {};
  try {
    // A15-N7: credit-specific 402 → separate event for InsufficientCreditsHandler
    if (detail.code === 'insufficient_credits') {
      window.dispatchEvent(new CustomEvent('aif:insufficient-credits', { detail }));
    } else {
      window.dispatchEvent(new CustomEvent('aif:upsell-needed', { detail }));
    }
  } catch { /* Silent */ }
}

export class ApiError extends Error {
  constructor(message, { status, body, idempotencyKey, cause } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.idempotencyKey = idempotencyKey;
    if (cause) this.cause = cause;
  }
  get isNetworkError() {
    return this.status === undefined || this.status === 0;
  }
  get isAuthError() {
    return this.status === 401 || this.status === 403;
  }
  get isClientError() {
    return this.status >= 400 && this.status < 500;
  }
  get isServerError() {
    return this.status >= 500;
  }
}

// =============================================================================
// IndexedDB Queue Integration (Layer C — A6 Sub-Phase 3 Step 6)
// =============================================================================
//
// Replaces the Sub-Phase 2 in-memory queueStub with persistent IndexedDB
// storage via offlineQueueDB. Writes queued while offline survive page
// reload and tab close.

import {
  enqueue as offlineEnqueue,
  getDepth as offlineGetDepth,
  clear as offlineClear,
  getCurrentScope,
} from './offlineQueueDB.js';

// =============================================================================
// Optimistic Concurrency (A6 Sub-Phase 5 — Block 5-MAIN-a)
// =============================================================================

/**
 * Build optimistic-concurrency headers + merge _base into the body.
 * No-op (returns empty headers + original body) when concurrency info absent.
 *
 * @param {{updatedAt: string, base?: object, editTime?: string}} conc
 * @param {object|null} body - the request body object
 * @returns {{headers: object, body: object|null}}
 */
function applyConcurrency(conc, body) {
  if (!conc || !conc.updatedAt) {
    return { headers: {}, body };
  }
  const nowIso = new Date().toISOString();
  const headers = {
    'If-Unmodified-Since': conc.updatedAt,
    'X-Client-Edit-Time': conc.editTime || nowIso,
    'X-Client-Sync-Time': nowIso,
  };
  let newBody = body;
  if (conc.base && typeof conc.base === 'object') {
    newBody = { ...(body || {}), _base: conc.base };
  }
  return { headers, body: newBody };
}

/**
 * Queue a write entry to IndexedDB.
 *
 * Called by request() when offline detection triggers queueing. Scope
 * (tenantId, userId) is resolved automatically via getCurrentScope().
 *
 * @param {object} entry - {id, idempotencyKey, method, url, body, headers, entityType}
 * @returns {Promise<string>} The idempotencyKey
 * @throws {Error} If user is not authenticated (no scope available)
 */
async function enqueueWrite(entry) {
  const scope = getCurrentScope();
  if (!scope) {
    throw new Error(
      'enqueueWrite: user not authenticated (aif_tenant_id or aif_user_id missing in localStorage)'
    );
  }
  const full = {
    ...entry,
    tenantId: scope.tenantId,
    userId: scope.userId,
    entityType: entry.entityType ?? 'unknown',
    clientRefs: entry.clientRefs ?? null,  // A6 Sub-Phase 4 Session 3: reference-rewriting
    // A6 Sub-Phase 5: persist concurrency context for offline replay
    originalState: entry.originalState ?? null,
    knownUpdatedAt: entry.knownUpdatedAt ?? null,
    editTime: entry.editTime ?? null,
  };
  await offlineEnqueue(full);
  return entry.idempotencyKey;
}

/**
 * Read the current queue depth. Scope-aware: returns depth for the current
 * user's queue. If opts.global=true, returns all entries.
 */
async function getQueueDepth(opts = {}) {
  const scope = opts.global ? null : getCurrentScope();
  return offlineGetDepth(scope ? { scope } : {});
}

/**
 * Clear queue entries. If opts.global=true, clears all entries.
 */
async function clearQueue(opts = {}) {
  const scope = opts.global ? null : getCurrentScope();
  return offlineClear(scope ? { scope } : {});
}

// Test-only: exported for apiClient.test.js integration tests.
export const __testHooks = { enqueueWrite, getQueueDepth, clearQueue };

/**
 * Detects whether we're likely offline.
 * navigator.onLine is unreliable on some setups but is a useful first check.
 */
function isLikelyOffline() {
  if (typeof navigator === 'undefined') return false;
  return navigator.onLine === false;
}

// =============================================================================
// Core request function (Layer A + B + C)
// =============================================================================

/**
 * Perform an HTTP request with all layers applied.
 *
 * @param {string} method - HTTP method (GET, POST, PATCH, DELETE)
 * @param {string} url - Endpoint path (joined with API_BASE)
 * @param {object} [opts]
 * @param {object} [opts.body] - Request body (will be JSON-stringified for writes)
 * @param {object} [opts.headers] - Extra headers (merged after auto-headers)
 * @param {number} [opts.timeout] - Timeout in ms (default 30s)
 * @param {boolean} [opts.queueOnOffline] - If true and offline, queue instead of throw (default false in Sub-Phase 2)
 * @param {AbortSignal} [opts.signal] - Optional abort signal (caller-provided)
 * @returns {Promise<{ok: true, data: any} | {ok: true, queued: true, idempotencyKey: string}>}
 * @throws {ApiError}
 */
/**
 * Convert FormData body into the entry shape persisted in IndexedDB.
 * Files/Blobs go into entry.blobs[]; string form fields go into entry.body.
 * Returns { body, blobs, bodyEncoding, totalBlobSize }.
 *
 * A6 Sub-Phase 6b: enables FormData uploads to survive page refresh.
 */
function splitFormDataForEnqueue(formData) {
  const body = {};
  const blobs = [];
  let totalBlobSize = 0;
  for (const [fieldName, value] of formData.entries()) {
    if (value instanceof Blob) {
      const filename = (value && value.name) || `${fieldName}-${Date.now()}`;
      blobs.push({
        fieldName,
        filename,
        mimeType: value.type || 'application/octet-stream',
        blob: value,
        size: value.size,
      });
      totalBlobSize += value.size || 0;
    } else {
      body[fieldName] = value;
    }
  }
  return { body, blobs, bodyEncoding: 'multipart', totalBlobSize };
}

async function request(method, url, opts = {}) {
  // A6 Sub-Phase 5: apply optimistic-concurrency headers + _base for writes
  if ((method === 'PATCH' || method === 'DELETE') && opts.concurrency) {
    const cc = applyConcurrency(opts.concurrency, opts.body ?? null);
    opts = { ...opts, body: cc.body, headers: { ...(opts.headers || {}), ...cc.headers } };
  }

  const isWrite = method !== 'GET';
  const idempotencyKey = isWrite ? generateIdempotencyKey() : undefined;
  const fullUrl = `${API_BASE}${url}`;

  // Build headers
  const headers = {};
  // FormData/Blob: browser sets multipart Content-Type with boundary automatically (Sub-Phase 6b)
  const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  const isBlob = typeof Blob !== 'undefined' && opts.body instanceof Blob;
  if (isWrite && opts.body !== undefined && !isFormData && !isBlob) {
    headers['Content-Type'] = 'application/json';
  }
  const token = tokenProvider();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (idempotencyKey) {
    headers['X-Idempotency-Key'] = idempotencyKey;
  }
  // Caller-provided headers win (e.g. override Content-Type for special endpoints)
  Object.assign(headers, opts.headers || {});

  // Layer C: pre-flight offline check (for writes only; reads benefit from cache via SW)
  if (isWrite && opts.queueOnOffline && isLikelyOffline()) {
    const enqueueEntry = {
      id: idempotencyKey,
      idempotencyKey,
      method,
      url: fullUrl,
      headers,
      entityType: opts.entityType ?? 'unknown',
      originalState: opts.concurrency?.base ?? null,
      knownUpdatedAt: opts.concurrency?.updatedAt ?? null,
      editTime: opts.concurrency?.editTime ?? null,
    };
    if (isFormData) {
      const { body: formBody, blobs, bodyEncoding } = splitFormDataForEnqueue(opts.body);
      enqueueEntry.body = formBody;
      enqueueEntry.blobs = blobs;
      enqueueEntry.bodyEncoding = bodyEncoding;
    } else {
      enqueueEntry.body = opts.body;
    }
    await enqueueWrite(enqueueEntry);
    return { ok: true, queued: true, idempotencyKey };
  }

  // Build fetch init
  const init = {
    method,
    headers,
  };
  if (opts.body !== undefined) {
    if (isFormData || isBlob) {
      init.body = opts.body;  // Sub-Phase 6b: browser encodes FormData/Blob natively
    } else {
      init.body = JSON.stringify(opts.body);
    }
  }

  // Timeout via AbortController (composed with caller signal if provided)
  const controller = new AbortController();
  const timeout = opts.timeout ?? DEFAULT_TIMEOUT_MS;
  const timer = setTimeout(() => controller.abort(), timeout);
  if (opts.signal) {
    if (opts.signal.aborted) controller.abort();
    else opts.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  init.signal = controller.signal;

  let response;
  try {
    response = await fetch(fullUrl, init);
  } catch (err) {
    clearTimeout(timer);
    // Network failure or abort. Layer C: optional queue.
    if (isWrite && opts.queueOnOffline) {
      const enqueueEntry = {
        id: idempotencyKey,
        idempotencyKey,
        method,
        url: fullUrl,
        headers,
        entityType: opts.entityType ?? 'unknown',
      };
      if (isFormData) {
        const { body: formBody, blobs, bodyEncoding } = splitFormDataForEnqueue(opts.body);
        enqueueEntry.body = formBody;
        enqueueEntry.blobs = blobs;
        enqueueEntry.bodyEncoding = bodyEncoding;
      } else {
        enqueueEntry.body = opts.body;
      }
      await enqueueWrite(enqueueEntry);
      return { ok: true, queued: true, idempotencyKey };
    }
    throw new ApiError(`Network error: ${err.message}`, {
      status: 0,
      idempotencyKey,
      cause: err,
    });
  }
  clearTimeout(timer);

  // Parse body (may be empty for 204; tolerate non-JSON for error pages)
  let body = null;
  const contentType = response.headers.get('content-type') || '';
  if (response.status !== 204) {
    if (contentType.includes('application/json')) {
      try {
        body = await response.json();
      } catch {
        body = null;
      }
    } else {
      try {
        body = await response.text();
      } catch {
        body = null;
      }
    }
  }

  if (!response.ok) {
    dispatchUpsellEvent(response.status, body);
    throw new ApiError(
      `API ${response.status} ${response.statusText || ''}`.trim(),
      { status: response.status, body, idempotencyKey }
    );
  }

  return { ok: true, data: body, idempotencyKey };
}

// =============================================================================
// Public API
//
// RESPONSE SHAPE (all methods):
//   { ok: true, data: <parsed body>, idempotencyKey: string|undefined }
//
// USAGE:
//   const result = await apiClient.post('/api/v1/aif/...', payload);
//   const body = result.data;            // <-- unwrap here
//   // OR: const { data } = await apiClient.post(...)
//
// COMMON BUG: treating `result` as the body directly.
//   WRONG: result.access_token          → undefined
//   RIGHT: result.data.access_token     → "eyJ..."
// =============================================================================

export const apiClient = {
  get(url, opts) {
    return request('GET', url, opts);
  },
  post(url, body, opts) {
    return request('POST', url, { ...opts, body });
  },
  put(url, body, opts) {
    return request('PUT', url, { ...opts, body });
  },
  patch(url, body, opts) {
    return request('PATCH', url, { ...opts, body });
  },
  delete(url, opts) {
    return request('DELETE', url, opts);
  },
};

// Default export for ergonomics (`import api from './lib/apiClient'`)
export default apiClient;
